import React, { useEffect, useState, useMemo } from "react";
import api from "../api/client";
import { Card, DataTable, PageHeader, Button, Badge, Money, StatCard } from "../components/ui";
import Modal, { FormField, FormRow, Input, Select, TextArea, SubmitButton, ErrorBanner } from "../components/Modal";
import CapitalSourcesPanel from "../components/CapitalSourcesPanel";
import FinTable from "../components/FinTable";

export default function Investors() {
  const [accounts, setAccounts] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [fundSummary, setFundSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/investors/lp-accounts/"),
      api.get("/investors/investments/"),
      api.get("/investors/lp-accounts/fund-summary/").catch(() => ({ data: null })),
    ]).then(([acctRes, invRes, fundRes]) => {
      setAccounts(acctRes.data.results || acctRes.data);
      setInvestments(invRes.data.results || invRes.data);
      setFundSummary(fundRes.data);
      setLoading(false);
    });
  };

  useEffect(load, []);

  /* Aggregate invested_amount per lp_account from DealInvestments */
  const investedByAccount = useMemo(() => {
    const map = {};
    investments.forEach((inv) => {
      const id = inv.lp_account;
      if (!map[id]) map[id] = { totalInvested: 0, dealCount: 0 };
      map[id].totalInvested += parseFloat(inv.invested_amount || 0);
      map[id].dealCount += 1;
    });
    return map;
  }, [investments]);

  /* Merge LP accounts with aggregated investment totals */
  const enrichedAccounts = useMemo(() => {
    return accounts.map((acct) => {
      const agg = investedByAccount[acct.id] || { totalInvested: 0, dealCount: 0 };
      const committed = parseFloat(acct.committed_capital || 0);
      const funded = parseFloat(acct.funded_capital || 0);
      return {
        ...acct,
        // Use committed_capital if set, otherwise fall back to aggregated investments
        display_committed: committed > 0 ? committed : agg.totalInvested,
        display_funded: funded > 0 ? funded : agg.totalInvested,
        deal_count: agg.dealCount,
      };
    });
  }, [accounts, investedByAccount]);

  const handleRowClick = (row) => {
    api.get(`/investors/lp-accounts/${row.id}/`).then((r) => setEditing(r.data));
  };

  return (
    <div>
      <PageHeader
        title="Investors"
        subtitle="LP capital accounts and fund summary"
        action={<Button onClick={() => setShowAdd(true)}>+ Add Investor</Button>}
      />

      {fundSummary && (
        <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
          <StatCard label="Total Invested" value={<Money value={fundSummary.total_invested} />} />
          <StatCard label="Total Distributed" value={<Money value={fundSummary.total_distributed} />} />
          {/* Not a NAV: invested less distributed, off the capital accounts
              (lp_accounting_engine). Labelled "Net Assets" it read as a third
              NAV that agreed with neither of the other two. */}
          <StatCard label="Net Capital (invested − distributed)" value={<Money value={fundSummary.net_assets} />} />
          <StatCard label="Active Investors" value={fundSummary.num_investors} />
          <StatCard label="Total Deals" value={fundSummary.num_deals} />
        </div>
      )}

      <Card title="LP Accounts">
        <DataTable
          columns={[
            { key: "investor_name", label: "Investor" },
            { key: "investor_type", label: "Type", render: (v) => v || "—" },
            { key: "entity_name", label: "Entity", render: (v) => v || "—" },
            { key: "display_committed", label: "Committed", align: "right", render: (v) => <Money value={v} /> },
            { key: "display_funded", label: "Funded", align: "right", render: (v) => <Money value={v} /> },
            { key: "deal_count", label: "Deals", align: "center", render: (v) => v || "—" },
            { key: "is_active", label: "Status", render: (v) => <Badge text={v ? "Active" : "Inactive"} variant={v ? "green" : "gray"} /> },
          ]}
          rows={enrichedAccounts}
          onRowClick={handleRowClick}
          emptyMessage="No investors yet — click + Add Investor"
          loading={loading}
        />
      </Card>

      {showAdd && <InvestorModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <InvestorModal investor={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function InvestorModal({ investor, onClose, onSaved }) {
  const isEdit = !!investor;
  const [entities, setEntities] = useState([]);
  const [form, setForm] = useState({
    investor_name: investor?.investor_name || "",
    investor_type: investor?.investor_type || "Family Office",
    entity: investor?.entity || "",
    committed_capital: investor?.committed_capital || "",
    funded_capital: investor?.funded_capital || "0",
    contact_name: investor?.contact_name || "",
    contact_email: investor?.contact_email || "",
    tax_id: investor?.tax_id || "",
    is_active: investor?.is_active !== undefined ? investor.is_active : true,
    notes: investor?.notes || "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/core/entities/").then((r) => setEntities(r.data.results || r.data));
  }, []);

  const set = (field) => (val) => setForm({ ...form, [field]: val });

  const save = () => {
    if (!form.investor_name || !form.entity || !form.committed_capital) {
      return setError("Investor name, entity, and committed capital are required");
    }
    setSaving(true);
    setError("");

    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) payload[k] = v;
    });

    const req = isEdit
      ? api.patch(`/investors/lp-accounts/${investor.id}/`, payload)
      : api.post("/investors/lp-accounts/", payload);

    req.then(() => onSaved())
      .catch((err) => {
        setSaving(false);
        const data = err.response?.data;
        setError(data ? JSON.stringify(data) : "Failed to save");
      });
  };

  return (
    <Modal title={isEdit ? `Edit — ${investor.investor_name}` : "Add Investor"} onClose={onClose}>
      <ErrorBanner message={error} />
      <FormField label="Investor Name" required>
        <Input value={form.investor_name} onChange={set("investor_name")} placeholder="e.g. Meridian Pension Fund" />
      </FormField>
      <FormRow>
        <FormField label="Investor Type" required>
          <Select value={form.investor_type} onChange={set("investor_type")} options={[
            { value: "Pension", label: "Pension" },
            { value: "Family Office", label: "Family Office" },
            { value: "Endowment", label: "Endowment" },
            { value: "Fund of Funds", label: "Fund of Funds" },
            { value: "HNWI", label: "HNWI" },
            { value: "Sovereign Wealth", label: "Sovereign Wealth" },
            { value: "Insurance", label: "Insurance" },
            { value: "Other", label: "Other" },
          ]} />
        </FormField>
        <FormField label="Entity" required>
          <Select value={form.entity} onChange={set("entity")}
            options={entities.map((e) => ({ value: e.id, label: e.name }))}
            placeholder="Select entity..."
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField label="Committed Capital" required>
          <Input type="number" value={form.committed_capital} onChange={set("committed_capital")} step="0.01" placeholder="10000000" />
        </FormField>
        <FormField label="Funded Capital">
          <Input type="number" value={form.funded_capital} onChange={set("funded_capital")} step="0.01" />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField label="Contact Name">
          <Input value={form.contact_name} onChange={set("contact_name")} placeholder="John Smith" />
        </FormField>
        <FormField label="Contact Email">
          <Input type="email" value={form.contact_email} onChange={set("contact_email")} placeholder="john@example.com" />
        </FormField>
      </FormRow>
      <FormField label="Tax ID">
        <Input value={form.tax_id} onChange={set("tax_id")} placeholder="XX-XXXXXXX" />
      </FormField>
      {isEdit && (
        <FormField label="Status">
          <Select value={form.is_active ? "true" : "false"} onChange={(v) => set("is_active")(v === "true")} options={[
            { value: "true", label: "Active" },
            { value: "false", label: "Inactive" },
          ]} />
        </FormField>
      )}
      <FormField label="Notes">
        <TextArea value={form.notes} onChange={set("notes")} placeholder="Optional notes" />
      </FormField>
      <SubmitButton onClick={save} loading={saving} label={isEdit ? "Save Changes" : "Create Investor"} />
      {investor && (
        <div style={{ marginTop: 16, borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: "#345479" }}>Capital Sources Across Deals</div>
          <CapitalSourcesPanel lpId={investor.id} />
        </div>
      )}
    </Modal>
  );
}
